import React, { useMemo } from "react";
import { Tooltip } from "@mui/material";
import moment from "moment";
import { STATUS, LEGEND, groupByMonth, hm, hmShort, timeOf, holidayNameOf } from "./helpers";
import { formatLocation, hasCoords } from "../../helpers/geolocation";
import "./Attendance.scss";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/* Whether a day can be applied for. A weekend or a holiday is nobody's leave
   to take, and a day already carrying a request — approved or still waiting —
   is spoken for. Everything else is fair game, including days already past:
   leave is often filed after the fact. */
export const canApply = (row) =>
    !row.request && !['weekend', 'holiday'].includes(row.status);

/* Why a day cannot be applied for, in words, so a cell that does not respond
   to a click says what it is rather than just ignoring you. */
export const applyBlockedBecause = (row) => {
    if (row.request) {
        const { typeName, status } = row.request;
        return status === 'approved'
            ? `${typeName} is already approved for this day.`
            : `${typeName} is already requested for this day, waiting on your manager.`;
    }
    if (row.status === 'weekend') return 'That is a weekly-off.';
    if (row.status === 'holiday') return `${holidayNameOf(row.dateKey)} is a holiday.`;
    return '';
};

/* What the tooltip says about a day, beyond its colour. */
const dayTitle = (row) => {
    /* a holiday is named rather than just labelled — the date alone does not
       say which one it is */
    const label = row.status === "holiday"
        ? holidayNameOf(row.dateKey)
        : (STATUS[row.status] || {}).label || row.status;

    const parts = [`${moment(row.date).format("ddd, DD MMM YYYY")} · ${label}`];

    if (row.grossMinutes) parts.push(`Worked ${hm(row.grossMinutes)}`);
    if (row.checkIn) parts.push(`${timeOf(row.checkIn)} – ${timeOf(row.checkOut)}`);

    if (hasCoords(row.checkInLocation)) parts.push(`In at ${formatLocation(row.checkInLocation)}`);
    else if (hasCoords(row.leaveLocation)) parts.push(`From ${formatLocation(row.leaveLocation)}`);

    if (row.request) {
        parts.push(row.request.status === 'approved'
            ? `${row.request.typeName} approved`
            : `${row.request.typeName} requested — waiting on your manager`);
    } else if (canApply(row)) {
        parts.push('Click to apply for leave or work from home');
    }

    return parts.join(" · ");
};

/* The range as months of squares: colour for the status, the hours worked in
   small text underneath, so a glance answers both questions at once. */
const AttendanceCalendar = ({ rows = [], onPickDay }) => {

    const months = useMemo(() => groupByMonth(rows), [rows]);

    const todayKey = moment().format("YYYY-MM-DD");

    if (!months.length) return null;

    return (
        <div className="att-calendar">

            <div className="att-cal-legend">
                {LEGEND.map((item) => (
                    <span key={item.status} className="att-cal-key">
                        <i className={`att-cal-swatch att-cal-swatch--${item.status}`} />
                        {item.label}
                    </span>
                ))}
                <span className="att-cal-key">
                    <i className="att-cal-swatch att-cal-swatch--weekend" />
                    Weekly-off
                </span>
            </div>

            <div className="att-cal-months">
                {months.map((month) => (
                    <div className="att-cal-month" key={month.key}>
                        <div className="att-cal-head">
                            <b>{month.label}</b>
                            <em>
                                {month.presentDays} {month.presentDays === 1 ? "day" : "days"}
                                {month.workedMinutes ? ` · ${hm(month.workedMinutes)}` : ""}
                            </em>
                        </div>

                        <div className="att-cal-grid">
                            {WEEKDAYS.map((weekday) => (
                                <span className="att-cal-weekday" key={weekday}>{weekday}</span>
                            ))}

                            {/* the blanks before the first of the month */}
                            {Array.from({ length: month.lead }).map((_, i) => (
                                <span className="att-cal-blank" key={`lead-${i}`} />
                            ))}

                            {month.days.map((row) => {
                                /* A day you can do something with is a button;
                                   one you cannot stays a span, so the keyboard
                                   only stops on the days that respond. */
                                const actionable = Boolean(onPickDay) && canApply(row);

                                const className = `att-cal-day att-cal-day--${row.status}`
                                    + (row.dateKey === todayKey ? " is-today" : "")
                                    + (row.request ? ` is-requested is-${row.request.status}` : "")
                                    + (actionable ? " is-actionable" : "");

                                const inner = (
                                    <>
                                        <b>{moment(row.date).format("D")}</b>
                                        <em>{hmShort(row.grossMinutes)}</em>
                                        {row.request && <i className="att-cal-flag" aria-hidden="true" />}
                                    </>
                                );

                                return (
                                    <Tooltip key={row.dateKey} title={dayTitle(row)} placement="top" arrow>
                                        {actionable
                                            ? <button type="button" className={className}
                                                onClick={() => onPickDay(row)}>
                                                {inner}
                                            </button>
                                            : <span className={className}>{inner}</span>}
                                    </Tooltip>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AttendanceCalendar;
